// @ts-nocheck
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

import { ReactComponent as DragIcon } from '../../../assets/DragIcon.svg';
import { ReactComponent as KeyboardArrowUpIcon } from '../../../assets/KeyboardArrowUpIcon.svg';
import { ReactComponent as KeyboardArrowDownIcon } from '../../../assets/KeyboardArrowDownIcon.svg';
import { ReactComponent as ForwardArrowIcon } from '../../../assets/ForwardArrowIcon.svg';

import SolyticsAutocomplete from '../../SolyticsAutocomplete';
import SolyticsButton from '../../SolyticsButton';
import SolyticsBox from '../../SolyticsBox';
import SolyticsTypography from '../../SolyticsTypography';
import SolyticsIconButton from '../../SolyticsIconButton';
import SolyticsNoDataPresent from '../../SolyticsNoDataPresent';
import SolyticsList from '../../SolyticsList';
import SolyticsGrid from '../../SolyticsGrid';

// Row height must match what the virtualizer estimates
const ITEM_HEIGHT = 38;

// ─── Column row content ───────────────────────────────────────────────────────
// Pure presentational component — renders the visual content of a single row.
// DnD wiring is owned entirely by SolyticsList / SolyticsListItem; this
// component only receives `isDragging` / `isOver` as visual hints if needed
// (SolyticsListItem already applies opacity + border-top for those states, so
// this component can stay fully presentational).

const ColumnRowContent = ({
  column,
  index,
  onMoveUp,
  onMoveDown,
  isOrderingDisabled,
  canMoveUp,
  canMoveDown,
  isSelected,
}) => {
  const displayText = column?.header || column?.id;

  return (
    <SolyticsBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: `${ITEM_HEIGHT}px`,
        width: '100%',
        borderRadius: '4px',
        '&:hover .column-move-arrows': {
          display: !isOrderingDisabled ? 'flex' : 'none',
        },
        '&:hover .column-index': {
          backgroundColor: (theme) => theme.palette.grey?.[70],
        },
        cursor: isOrderingDisabled ? 'default' : 'grab',
      }}
    >
      <DragIcon
        style={{
          width: '18px',
          height: '18px',
          color: '#727272',
          flexShrink: 0,
          opacity: isOrderingDisabled ? 0.4 : 1,
        }}
      />
      <SolyticsBox
        className="column-index"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: '4px',
          flexShrink: 0,
          backgroundColor: (theme) =>
            isSelected
              ? theme?.palette?.grey?.[70]
              : theme?.palette?.grey?.[30],
        }}
      >
        <SolyticsTypography
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '14px',
            color: 'text.primary',
            textAlign: 'center',
          }}
        >
          {index + 1}
        </SolyticsTypography>
      </SolyticsBox>

      <SolyticsTypography
        noWrap
        variant="body1"
        sx={{ flex: 1, color: 'text.primary' }}
      >
        {displayText}
      </SolyticsTypography>

      <SolyticsBox
        className="column-move-arrows"
        sx={{
          display: 'none',
          gap: '4px',
          alignItems: 'center',
          flexShrink: 0,
          marginLeft: 'auto',
        }}
      >
        <SolyticsIconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onMoveUp(index);
          }}
          disabled={!canMoveUp}
          aria-label="Move column up"
        >
          <KeyboardArrowUpIcon
            style={{
              height: '20px',
              width: '20px',
              color: (theme) => theme.palette.grey.T10,
            }}
          />
        </SolyticsIconButton>
        <SolyticsIconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onMoveDown(index);
          }}
          disabled={!canMoveDown}
          aria-label="Move column down"
        >
          <KeyboardArrowDownIcon
            style={{
              height: '20px',
              width: '20px',
              color: (theme) => theme.palette.grey.T10,
            }}
          />
        </SolyticsIconButton>
      </SolyticsBox>
    </SolyticsBox>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * SolyticsTableColumnOrdering - Column reordering panel with drag-and-drop and quick move
 *
 * @param {Object}   props
 * @param {Array}    props.columns                   Column info objects { id, header }
 * @param {Array}    props.pendingColumnOrder         Current pending column order (array of IDs)
 * @param {Function} props.setPendingColumnOrder      Setter for pending column order
 * @param {Object}   props.pendingColumnVisibility    Pending visibility to filter visible-only columns
 * @param {number}  props.listVirtualizationThreshold       Enable virtual list for large column counts
 */
const SolyticsTableColumnOrdering = ({
  columns = [],
  pendingColumnOrder,
  setPendingColumnOrder,
  pendingColumnVisibility = {},
  listVirtualizationThreshold,
}) => {
  const [orderedColumns, setOrderedColumns] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [orderValue, setOrderValue] = useState(null);
  const [selectedColumnId, setSelectedColumnId] = useState(null);

  const isColumnOrderingDisabled = useCallback((column) => {
    if (!column) return false;
    return Boolean(column?.disabledOrderingCols);
  }, []);

  // Build visible, ordered column list only when columns or visibility changes
  useEffect(() => {
    const visibleColumns = columns.filter((col) => {
      const isVisible =
        pendingColumnVisibility[col.id] !== undefined
          ? pendingColumnVisibility[col.id]
          : true;
      return isVisible;
    });

    if (pendingColumnOrder && pendingColumnOrder.length > 0) {
      const ordered = [];
      pendingColumnOrder.forEach((colId) => {
        const col = visibleColumns.find((c) => c.id === colId);
        if (col) ordered.push(col);
      });
      visibleColumns.forEach((col) => {
        if (!pendingColumnOrder.includes(col.id)) ordered.push(col);
      });
      setOrderedColumns(ordered);
    } else {
      setOrderedColumns(visibleColumns);
    }

    setSearchColumn(null);
    setOrderValue(null);
  }, [columns, pendingColumnVisibility]);

  const canMoveToIndex = useCallback(
    (sourceIndex, targetIndex) => {
      if (
        sourceIndex < 0 ||
        targetIndex < 0 ||
        sourceIndex >= orderedColumns.length ||
        targetIndex >= orderedColumns.length
      ) {
        return false;
      }

      const sourceColumn = orderedColumns[sourceIndex];
      const targetColumn = orderedColumns[targetIndex];

      if (!sourceColumn || !targetColumn) return false;

      return (
        !isColumnOrderingDisabled(sourceColumn) &&
        !isColumnOrderingDisabled(targetColumn)
      );
    },
    [orderedColumns, isColumnOrderingDisabled]
  );

  const moveListItem = useCallback(
    (dragIndex, destIndex) => {
      if (!canMoveToIndex(dragIndex, destIndex)) return;

      const newColumns = [...orderedColumns];
      const draggedItem = newColumns[dragIndex];
      setSelectedColumnId(draggedItem.id);
      newColumns.splice(dragIndex, 1);
      newColumns.splice(destIndex, 0, draggedItem);
      setOrderedColumns(newColumns);
      setPendingColumnOrder(newColumns.map((col) => col.id));
    },
    [orderedColumns, setPendingColumnOrder, canMoveToIndex]
  );

  const handleMoveUp = useCallback(
    (index) => {
      if (index > 0) moveListItem(index, index - 1);
    },
    [moveListItem]
  );

  const handleMoveDown = useCallback(
    (index) => {
      if (index < orderedColumns.length - 1) moveListItem(index, index + 1);
    },
    [moveListItem, orderedColumns]
  );

  const handleQuickMove = useCallback(() => {
    if (!searchColumn || !orderValue) return;

    const columnIndex = orderedColumns.findIndex(
      (col) => col.id === searchColumn.value
    );

    if (columnIndex !== -1) {
      const targetIndex = orderValue.value - 1;
      if (
        targetIndex >= 0 &&
        targetIndex < orderedColumns.length &&
        canMoveToIndex(columnIndex, targetIndex)
      ) {
        moveListItem(columnIndex, targetIndex);
      }
    }
  }, [searchColumn, orderValue, orderedColumns, moveListItem, canMoveToIndex]);

  const columnOptions = useMemo(
    () =>
      orderedColumns
        .filter((col) => !isColumnOrderingDisabled(col))
        .map((col) => ({ label: col.header || col.id, value: col.id })),
    [orderedColumns, isColumnOrderingDisabled]
  );

  const positionOptions = useMemo(
    () =>
      orderedColumns
        .map((col, idx) => ({
          label: String(idx + 1),
          value: idx + 1,
          isDisabled: isColumnOrderingDisabled(col),
        }))
        .filter((option) => !option.isDisabled)
        .map(({ label, value }) => ({ label, value })),
    [orderedColumns, isColumnOrderingDisabled]
  );

  // Build the SolyticsList items array.
  const listItems = useMemo(
    () =>
      orderedColumns.map((column, index) => ({
        id: column.id,
        selected: column.id === selectedColumnId,
        disabled: isColumnOrderingDisabled(column),
        custom: (
          <ColumnRowContent
            column={column}
            index={index}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            isOrderingDisabled={isColumnOrderingDisabled(column)}
            canMoveUp={index > 0 && canMoveToIndex(index, index - 1)}
            canMoveDown={
              index < orderedColumns.length - 1 &&
              canMoveToIndex(index, index + 1)
            }
            isSelected={column.id === selectedColumnId}
          />
        ),
      })),
    [
      orderedColumns,
      handleMoveUp,
      handleMoveDown,
      isColumnOrderingDisabled,
      canMoveToIndex,
      selectedColumnId,
    ]
  );

  return (
    <SolyticsBox
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Quick Move Section */}
      <SolyticsBox
        sx={{
          backgroundColor: (theme) => theme.palette.grey?.[30],
          borderBottom: (theme) =>
            `1px solid ${theme.palette.grey?.[50] || theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <SolyticsBox
          sx={{
            px: 2,
            pt: '12px',
            pb: '16px',
            gap: '8px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SolyticsTypography
            variant="subtitle1"
            sx={{ color: 'text.primary', mb: '10px' }}
          >
            Quick Move (Select and Reorder Column)
          </SolyticsTypography>

          <SolyticsBox
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <SolyticsBox sx={{ flex: 1 }}>
              <SolyticsAutocomplete
                noOptionsText="No such columns"
                name="search-column"
                value={searchColumn}
                onChange={setSearchColumn}
                options={columnOptions}
                placeholder="Search Column"
                fullWidth
              />
            </SolyticsBox>

            <SolyticsBox
              sx={{
                color: (theme) => theme.palette.grey.T10,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ForwardArrowIcon
                style={{ width: '18px', height: '18px', flexShrink: 0 }}
              />
            </SolyticsBox>

            <SolyticsBox sx={{ width: '100px' }}>
              <SolyticsAutocomplete
                noOptionsText="No such positions"
                name="order-value"
                value={orderValue}
                onChange={setOrderValue}
                options={positionOptions}
                placeholder="Order"
                fullWidth
              />
            </SolyticsBox>

            <SolyticsButton
              variant="contained"
              onClick={handleQuickMove}
              disabled={!searchColumn || !orderValue}
              size="medium"
            >
              Move
            </SolyticsButton>
          </SolyticsBox>
        </SolyticsBox>
      </SolyticsBox>

      {/* Column List */}

      {orderedColumns.length === 0 ? (
        <SolyticsGrid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <SolyticsNoDataPresent message="No columns found" />
        </SolyticsGrid>
      ) : (
        <SolyticsBox
          flex={1}
          minHeight={0}
          overflow="auto"
          paddingX={2}
          paddingY={1}
        >
          <SolyticsList
            disablePadding
            items={listItems}
            gap={0}
            listVirtualizationThreshold={listVirtualizationThreshold}
            enableDragAndDrop
            onReorder={moveListItem}
          />
        </SolyticsBox>
      )}
    </SolyticsBox>
  );
};

SolyticsTableColumnOrdering.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      header: PropTypes.string,
      disabledOrderingCols: PropTypes.bool,
    })
  ).isRequired,
  pendingColumnOrder: PropTypes.arrayOf(PropTypes.string),
  setPendingColumnOrder: PropTypes.func.isRequired,
  pendingColumnVisibility: PropTypes.objectOf(PropTypes.bool),
  listVirtualizationThreshold: PropTypes.number,
};

export default SolyticsTableColumnOrdering;
